import {
  AlertCircle,
  Archive,
  CheckCircle,
  Clock,
  Download,
  Edit,
  Eye,
  FileCheck,
  FileImage,
  MapPin,
} from 'lucide-react';
import React, { useState } from 'react';
import { DrawingCategory, DrawingStatus, ProjectDrawing } from '../../types';

interface DrawingCardProps {
  drawing: ProjectDrawing;
  viewMode: 'grid' | 'list';
  isSelected?: boolean;
  onSelect?: (drawingId: string) => void;
  onView?: (drawing: ProjectDrawing) => void;
  onEdit?: (drawing: ProjectDrawing) => void;
  onDownload?: (drawing: ProjectDrawing) => void;
  onDelete?: (drawing: ProjectDrawing) => void;
  onComment?: (drawing: ProjectDrawing) => void;
  showProject?: boolean;
}

const DrawingCard: React.FC<DrawingCardProps> = ({
  drawing,
  viewMode,
  isSelected = false,
  onSelect,
  onView,
  onEdit,
  onDownload,
  // onDelete: _onDelete,
  // onComment: _onComment,
  showProject = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  // Get status info
  const getStatusInfo = (status: DrawingStatus) => {
    switch (status) {
      case 'draft':
        return {
          icon: Edit,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          label: 'Draft',
        };
      case 'under_review':
        return {
          icon: Clock,
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          label: 'Under Review',
        };
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'bg-green-100 text-green-800 border-green-200',
          label: 'Approved',
        };
      case 'superseded':
        return {
          icon: AlertCircle,
          color: 'bg-orange-100 text-orange-800 border-orange-200',
          label: 'Superseded',
        };
      case 'archived':
        return {
          icon: Archive,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: 'Archived',
        };
      default:
        return {
          icon: FileImage,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          label: status,
        };
    }
  };

  // Get category color
  const getCategoryColor = (category: DrawingCategory) => {
    const colorMap: Record<DrawingCategory, string> = {
      floor_plan: 'bg-red-100 text-red-800',
      elevation: 'bg-green-100 text-green-800',
      section: 'bg-yellow-100 text-yellow-800',
      detail: 'bg-purple-100 text-purple-800',
      site_plan: 'bg-indigo-100 text-indigo-800',
      electrical: 'bg-red-100 text-red-800',
      plumbing: 'bg-cyan-100 text-cyan-800',
      hvac: 'bg-orange-100 text-orange-800',
      structural: 'bg-gray-100 text-gray-800',
      landscape: 'bg-green-100 text-green-800',
      fire_safety: 'bg-red-100 text-red-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  };

  // Get file type icon and color
  const getFileTypeInfo = () => {
    const extension = drawing.file_info.file_extension.toLowerCase();
    switch (extension) {
      case '.pdf':
        return { icon: '📄', color: 'bg-red-100 text-red-800' };
      case '.dwg':
      case '.dxf':
        return { icon: '📐', color: 'bg-red-100 text-red-800' };
      case '.jpg':
      case '.jpeg':
      case '.png':
        return { icon: '🖼️', color: 'bg-green-100 text-green-800' };
      case '.vsd':
        return { icon: '📊', color: 'bg-purple-100 text-purple-800' };
      default:
        return { icon: '📄', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const statusInfo = getStatusInfo(drawing.status);
  const fileTypeInfo = getFileTypeInfo();
  const StatusIcon = statusInfo.icon;

  if (viewMode === 'grid') {
    return (
      <div
        className={`bg-white border rounded-lg hover:shadow-md transition-shadow duration-200 group ${
          isSelected
            ? 'border-brand-primary ring-1 ring-blue-200'
            : 'border-gray-200 hover:border-gray-300'
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start justify-between mb-3">
            {onSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(drawing._id)}
                className="h-4 w-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
              />
            )}

            <div
              className={`flex items-center gap-1 ${isHovered ? 'opacity-100' : 'opacity-60'} transition-opacity`}
            >
              {onView && (
                <button
                  onClick={() => onView(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="View"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              {onDownload && (
                <button
                  onClick={() => onDownload(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
              {onEdit && drawing.status === 'draft' && (
                <button
                  onClick={() => onEdit(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* File preview */}
          <div className="aspect-[4/3] bg-gray-50 rounded border flex items-center justify-center relative overflow-hidden">
            {drawing.file_info.mime_type.startsWith('image/') ? (
              <img
                src={drawing.file_url}
                alt={drawing.name}
                className="w-full h-full object-cover"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.classList.remove('hidden');
                  }
                }}
              />
            ) : null}
            <div className="flex flex-col items-center text-gray-400">
              <span className="text-2xl mb-2">{fileTypeInfo.icon}</span>
              <span className="text-sm font-medium">
                {drawing.file_info.file_extension.toUpperCase()}
              </span>
              <span className="text-xs text-gray-500 mt-1">
                {formatFileSize(drawing.file_info.file_size)}
              </span>
            </div>

            {/* Status badge */}
            <div className="absolute top-2 left-2">
              <span
                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}
              >
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusInfo.label}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div>
            <h3 className="font-medium text-gray-900 truncate" title={drawing.name}>
              {drawing.name}
            </h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span>v{drawing.metadata.version}</span>
              {drawing.metadata.revision && <span>Rev. {drawing.metadata.revision}</span>}
              {drawing.metadata.drawing_number && <span>#{drawing.metadata.drawing_number}</span>}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(drawing.category)}`}
            >
              {drawing.category.replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-500 uppercase">{drawing.metadata.discipline}</span>
          </div>

          {showProject && drawing.project_id_populated && (
            <div className="flex items-center text-sm text-gray-600">
              <FileCheck className="h-3 w-3 mr-1" />
              <span className="truncate">{drawing.project_id_populated.name}</span>
            </div>
          )}

          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-3 w-3 mr-1" />
            <span className="truncate">
              {[drawing.location.site, drawing.location.building, drawing.location.floor]
                .filter(Boolean)
                .join(' → ')}
            </span>
          </div>

          {drawing.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {drawing.tags.slice(0, 2).map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  {tag}
                </span>
              ))}
              {drawing.tags.length > 2 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                  +{drawing.tags.length - 2}
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs text-gray-500">
            <span>{drawing.uploaded_by.firstName || drawing.uploaded_by.name || 'Unknown'}</span>
            <span>{formatDate(drawing.uploaded_at)}</span>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div
      className={`bg-white border rounded-lg hover:shadow-md transition-shadow duration-200 ${
        isSelected
          ? 'border-brand-primary ring-1 ring-blue-200'
          : 'border-gray-200 hover:border-gray-300'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {onSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(drawing._id)}
                className="h-4 w-4 text-brand-primary border-gray-300 rounded focus:ring-brand-primary"
              />
            )}

            <div className="relative">
              <div className="w-10 h-10 bg-gray-50 rounded border flex items-center justify-center">
                <span className="text-lg">{fileTypeInfo.icon}</span>
              </div>
              <div className="absolute -top-1 -right-1">
                <span
                  className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}
                >
                  <StatusIcon className="h-2.5 w-2.5" />
                </span>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate" title={drawing.name}>
                {drawing.name}
              </h3>
              <div className="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                <span>v{drawing.metadata.version}</span>
                {drawing.metadata.revision && <span>Rev. {drawing.metadata.revision}</span>}
                {drawing.metadata.drawing_number && <span>#{drawing.metadata.drawing_number}</span>}
                <span className="text-xs uppercase">{drawing.metadata.discipline}</span>
                <span className="text-xs">{formatFileSize(drawing.file_info.file_size)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(drawing.category)}`}
            >
              {drawing.category.replace('_', ' ')}
            </span>

            <div
              className={`flex items-center gap-1 ${isHovered ? 'opacity-100' : 'opacity-60'} transition-opacity`}
            >
              {onView && (
                <button
                  onClick={() => onView(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="View"
                >
                  <Eye className="h-4 w-4" />
                </button>
              )}
              {onDownload && (
                <button
                  onClick={() => onDownload(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
              {onEdit && drawing.status === 'draft' && (
                <button
                  onClick={() => onEdit(drawing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-gray-600">
                <MapPin className="h-3 w-3 mr-1" />
                <span className="truncate">
                  {[drawing.location.site, drawing.location.building, drawing.location.floor]
                    .filter(Boolean)
                    .join(' → ')}
                </span>
              </div>
              {drawing.tags.length > 0 && (
                <div className="flex space-x-1">
                  {drawing.tags.slice(0, 2).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {drawing.tags.length > 2 && (
                    <span className="text-xs text-gray-500">+{drawing.tags.length - 2}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center text-xs text-gray-500">
              <span className="mr-3">
                {drawing.uploaded_by.firstName || drawing.uploaded_by.name || 'Unknown'}
              </span>
              <span>{formatDate(drawing.uploaded_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawingCard;
